import { createContext, useContext, useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { AlertTriangle } from "lucide-react"
import { emailAuth } from "../api/emailAuth"
import { loginUser, registerUser } from "../api/auth/post"
import { resetTokenExpiredState, TOKEN_EXPIRED_EVENT } from "../api/api"

const AuthContext = createContext<any>(null)

export function AuthProvider({ children }: any) {
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false)

  const clearSession = () => {
    localStorage.clear()
    setUser(null)
  }

  const logout = () => {
    clearSession()
    resetTokenExpiredState()
  }

  const handleSessionExpired = () => {
    setSessionExpiredOpen(true)
  }

  const handleLoginAgain = () => {
    clearSession()
    setSessionExpiredOpen(false)
    resetTokenExpiredState()
    window.location.href = "/login"
  }

  const register = async (data: any) => {
    const res = data.email ? await emailAuth("register/email", data) : await registerUser(data)

    const { user, store } = res

    if (store?._id) localStorage.setItem("currentStoreId", store._id)
    setUser(user)

    return res
  }

  const login = async ({ phone, email, password }: any) => {
    const res = email ? await emailAuth("login/email", { email, password }) : await loginUser({ phone, password })

    const { requestOtp, user } = res

    if (!requestOtp) setUser(user)

    return res
  }

  const verifyUser = (token: any, store: any) => {
    if (!token) throw new Error("Session invalide")
    setUser((current: any) => ({ ...current, token }))
    localStorage.setItem("token", token)
    if (store?._id) localStorage.setItem("currentStoreId", store._id)
    setSessionExpiredOpen(false)
    resetTokenExpiredState()
  }

  useEffect(() => {
    const token = localStorage.getItem("token")

    if (token) {
      setUser({ token })
    }

    setLoading(false)
  }, [])

  useEffect(() => {
    window.addEventListener(TOKEN_EXPIRED_EVENT, handleSessionExpired)
    return () => window.removeEventListener(TOKEN_EXPIRED_EVENT, handleSessionExpired)
  }, [])

  const isAuthenticated = !!user && !!localStorage.getItem("token")

  return (
    <AuthContext.Provider value={{ user, register, verifyUser, login, logout, isAuthenticated, loading }}>
      {children}
      <SessionExpiredModal isOpen={sessionExpiredOpen} onLoginAgain={handleLoginAgain} />
    </AuthContext.Provider>
  )
}

function SessionExpiredModal({
  isOpen,
  onLoginAgain,
}: {
  isOpen: boolean
  onLoginAgain: () => void
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-full max-w-md overflow-hidden rounded-3xl border border-white/40 bg-white shadow-2xl"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.98 }}
          >
            <div className="bg-gradient-to-r from-rose-600 to-orange-500 px-5 py-5 text-white">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-white/15 p-2.5">
                  <AlertTriangle size={18} />
                </div>
                <div>
                  <p className="text-lg font-semibold">Session expirée</p>
                  <p className="mt-1 text-sm text-white/90">
                    Votre token n'est plus valide. Veuillez vous reconnecter pour continuer.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-5 py-5">
              <button
                onClick={onLoginAgain}
                className="inline-flex w-full items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Se reconnecter
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export const useAuth = () => useContext(AuthContext)
